import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  FileText,
  Download,
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { intelligenceAPI } from '../services/api';

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [downloadingPDF, setDownloadingPDF] = useState(null);
  const itemsPerPage = 20;

  const markets = [
    { code: 'all', label: 'Tous les marchés' },
    { code: 'VN', label: 'Vietnam' },
    { code: 'IN', label: 'Inde' },
    { code: 'JK', label: 'Indonésie' },
    { code: 'PH', label: 'Philippines' },
    { code: 'KRX', label: 'Corée du Sud' },
    { code: 'WSE', label: 'Pologne' }
  ];

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      if (selectedMarket !== 'all') {
        params.market = selectedMarket;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const data = await intelligenceAPI.getReports(params);
      setReports(data.reports || []);
      setTotalReports(data.total || data.reports?.length || 0);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedMarket, searchQuery]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleDownloadPDF = async (market, symbol, name) => {
    setDownloadingPDF(`${market}/${symbol}`);
    try {
      const blob = await intelligenceAPI.downloadPDF(market, symbol);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-${symbol}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPDF(null);
    }
  };

  const getRecommendationBadge = (recommendation) => {
    const styles = {
      'BUY': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'STRONG BUY': 'bg-emerald-500/30 text-emerald-300 border-emerald-500/40',
      'HOLD': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'SELL': 'bg-red-500/20 text-red-400 border-red-500/30',
      'STRONG SELL': 'bg-red-500/30 text-red-300 border-red-500/40'
    };
    const icons = {
      'BUY': <TrendingUp className="w-3 h-3" />,
      'STRONG BUY': <TrendingUp className="w-3 h-3" />,
      'HOLD': <Minus className="w-3 h-3" />,
      'SELL': <TrendingDown className="w-3 h-3" />,
      'STRONG SELL': <TrendingDown className="w-3 h-3" />
    };
    
    return (
      <Badge className={`${styles[recommendation] || 'bg-gray-500/20 text-gray-400'} flex items-center gap-1`}>
        {icons[recommendation]}
        {recommendation}
      </Badge>
    );
  };

  const totalPages = Math.ceil(totalReports / itemsPerPage);

  return (
    <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Rapports d'Analyse</h1>
          <p className="text-gray-400">
            Analyses détaillées générées par IA pour {totalReports.toLocaleString()} actions
          </p>
        </div>

        {/* Filters */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom ou symbole..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {markets.map((market) => (
                  <Button
                    key={market.code}
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedMarket(market.code);
                      setCurrentPage(1);
                    }}
                    className={`${
                      selectedMarket === market.code
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {market.label}
                  </Button>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchReports}
                className="text-gray-400 hover:text-white"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reports List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
          </div>
        ) : error ? (
          <Card className="bg-red-500/10 border-red-500/20">
            <CardContent className="p-6 text-center">
              <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
              <p className="text-red-400">{error}</p>
              <Button onClick={fetchReports} className="mt-4" variant="outline">
                Réessayer
              </Button>
            </CardContent>
          </Card>
        ) : reports.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-10 text-center">
              <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">Aucun rapport trouvé</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid gap-4">
              {reports.map((report) => (
                <Card 
                  key={`${report.market}-${report.symbol}`}
                  className="bg-white/5 border-white/10 hover:border-white/20 transition-colors"
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Link 
                            to={`/stock/${report.market}-${report.symbol}`}
                            className="text-white font-semibold hover:text-emerald-400 transition-colors"
                          >
                            {report.name}
                          </Link>
                          <Badge variant="outline" className="text-xs border-white/20 text-gray-400">
                            {report.symbol}
                          </Badge>
                          <Badge variant="outline" className="text-xs border-white/20 text-gray-400">
                            {report.market}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span>{report.sector}</span>
                          <span>•</span>
                          <span>{report.country}</span>
                          {report.generated_at && (
                            <>
                              <span>•</span>
                              <span>
                                {new Date(report.generated_at).toLocaleDateString('fr-FR')}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {report.recommendation && getRecommendationBadge(report.recommendation)}
                        
                        <Link to={`/reports/${report.market}/${report.symbol}`}>
                          <Button variant="outline" size="sm" className="border-white/20 text-gray-300 hover:text-white">
                            <FileText className="w-4 h-4 mr-2" />
                            Voir le rapport
                          </Button>
                        </Link>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPDF(report.market, report.symbol, report.name)}
                          disabled={downloadingPDF === `${report.market}/${report.symbol}`}
                          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        >
                          {downloadingPDF === `${report.market}/${report.symbol}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="border-white/20 text-gray-400"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-gray-400 px-4">
                  Page {currentPage} sur {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="border-white/20 text-gray-400"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
